import { i18n } from '@/plugins/i18n';
import { type ApiBudgetAthlete, type ApiBudgetAthleteLineItem, BudgetAthlete } from '@/types/athletes';
import { turnTwrnIntoId, convertPercentageToDecimal, convertCentsToDollarsDecimal } from '@/utils/helpers';
import { sumBy } from 'lodash-es';
import type { ApiAthleteTag } from './tags';
import type { ApiPaginationMetadata } from './api';
import { formatDateWithTime } from '@/utils/dates';
import { TeamPosition } from '@/types/positions';


export interface ApiTeamBudgetPositionTarget {
  total_budget: number;
  position: string;
  position_id: string;
};

export interface ApiTeamBudgetList {
  data: ApiTeamBudgetOverview[];
}

export interface ApiTeamBudgetOverview {
  name: string;
  status: BudgetStatusEnum;
  total_budget: number;
  scholarships: number;
  updated_at: string;
  academic_year_date_id: string;
  scholarship_value: number;
  created_at: string;
  roster_size: number;
  twrn: string;
}

export const BUDGET_STATUS_ENUM = {
  DRAFT: 'draft',
  ACTIVE: 'active',
} as const;

export type BudgetStatusEnum = typeof BUDGET_STATUS_ENUM[keyof typeof BUDGET_STATUS_ENUM];

export interface CreateBudgetRequestModel {
  name: string;
  academic_year_date_id?: string;
  total_budget: number;
  scholarships: number;
  roster_size: number;
  scholarship_value: number;
  third_party_target?: number;
}

export interface UpdateTeamBudgetLineItemRequestModel {
  athletes: ApiTeamBudgetLineItem[];
}

export interface UpdateOrderingRequestModel {
  athlete_id: string;
  order: number;
}
export interface ApiTeamBudgetLineItem {
  athlete_twrn: string;
  line_item: ApiBudgetAthleteLineItem;
}

export class TeamBudgetOverview {
  createdAt: string;
  name: string;
  rosterSize: number;
  scholarships: number;
  status: BudgetStatusEnum;
  totalBudget: number;
  scholarshipValue: number;
  twrn: string;
  updatedAt: string;
  id: string;
  academicYear: string;
  teamTwrn?: string;
  academicYearDateId: string;

  constructor(data: ApiTeamBudgetOverview, teamTwrn?: string) {
    this.createdAt = data.created_at;
    this.name = data.name;
    this.rosterSize = data.roster_size;
    this.scholarships = convertPercentageToDecimal(data.scholarships);
    this.status = data.status;
    this.totalBudget = convertCentsToDollarsDecimal(data.total_budget);
    this.twrn = data.twrn;
    this.updatedAt = data.updated_at;
    this.id = turnTwrnIntoId(data.twrn);
    this.academicYearDateId = data.academic_year_date_id;
    this.scholarshipValue = convertCentsToDollarsDecimal(data.scholarship_value);
    this.teamTwrn = teamTwrn;

    this.academicYear = ''; // Initialize as empty, will be populated via UUID lookup
  }

  static mapBudgetToFormFields(budget: TeamBudgetOverview, teamTwrn?: string) {
    return {
      name: budget.name,
      academicYear: budget.academicYearDateId, // Use UUID instead of string
      team: teamTwrn,
      totalBudget: budget.totalBudget,
      scholarships: budget.scholarships,
      scholarshipValue: budget.scholarshipValue,
      rosterSize: budget.rosterSize,
    };
  }

  static clone(instance: TeamBudgetOverview): TeamBudgetOverview {
    const cloned = Object.create(TeamBudgetOverview.prototype) as TeamBudgetOverview;
    cloned.createdAt = instance.createdAt;
    cloned.name = instance.name;
    cloned.rosterSize = instance.rosterSize;
    cloned.scholarships = instance.scholarships;
    cloned.status = instance.status;
    cloned.totalBudget = instance.totalBudget;
    cloned.scholarshipValue = instance.scholarshipValue;
    cloned.twrn = instance.twrn;
    cloned.updatedAt = instance.updatedAt;
    cloned.id = instance.id;
    cloned.academicYear = instance.academicYear;
    cloned.teamTwrn = instance.teamTwrn;
    cloned.academicYearDateId = instance.academicYearDateId;
    return cloned;
  }
}


export class PositionBudget {
  athletes: BudgetAthlete[];
  allocationValue: number;
  allocationPercentage: number;
  allocationSetByUser: boolean;
  athleteCount: number;
  budgetRemaining: number;
  committed: number;
  position: string;
  rosterSize: number;
  totalScholarships: number;
  totalBudget: number;
  totalScholarshipsAllocation: number;
  totalNilEarnings: number;
  budgetAllocated: number;
  totalScholarshipsNew: number;
  totalScholarshipsNewAllocation: number;
  constructor(athletes: ApiBudgetAthlete[], tagDefinitions: ApiAthleteTag[], positionTarget?: ApiTeamBudgetPositionTarget, totalTeamBudget?: number, teamTwrn?: string) {
    this.athletes = athletes
      .map((athlete) => new BudgetAthlete(athlete, tagDefinitions, teamTwrn))
      .sort((a, b) => {
      // Compare by `order` first
        if (a.order !== null && b.order !== null) {
          return a.order - b.order;
        }
        if (a.order !== null) return -1;
        if (b.order !== null) return 1;

        // If `order` is null for both, compare by `lastName`
        return a.lastName.localeCompare(b.lastName);
      });
    this.committed = sumBy(this.athletes, 'allocation');
    this.allocationValue = convertCentsToDollarsDecimal(positionTarget?.total_budget ?? 0);
    this.allocationPercentage = (totalTeamBudget && totalTeamBudget > 0)
      ? (this.allocationValue / totalTeamBudget) * 100
      : 0;
    this.allocationSetByUser = !!positionTarget;
    this.athleteCount = athletes.length;
    this.budgetRemaining = this.allocationValue - this.committed;
    const resolvedFromTarget = teamTwrn
      ? TeamPosition.resolveName(teamTwrn, positionTarget?.position_id ?? null)
      : null;
    this.position = resolvedFromTarget ?? this.athletes[0]?.position ?? i18n.global.t('unspecified');
    this.totalScholarships = sumBy(this.athletes, 'scholarshipPercentage');
    this.totalScholarshipsNew = convertPercentageToDecimal(sumBy(this.athletes, 'equivalencyPercentage'));
    this.totalBudget = totalTeamBudget ?? 0;
    this.rosterSize = sumBy(this.athletes, 'rosterSpot');
    this.totalScholarshipsAllocation = sumBy(this.athletes, 'scholarshipAllocation');
    this.totalScholarshipsNewAllocation = convertCentsToDollarsDecimal(sumBy(this.athletes, 'scholarshipCalculatedValue'));
    this.totalNilEarnings = sumBy(this.athletes, 'nilEarnings');
    this.budgetAllocated = sumBy(this.athletes, 'allocation');
  }

  /**
   * Creates a new PositionBudget instance by copying all properties from an existing instance.
   * This preserves the class prototype (instanceof checks work) unlike cloneDeep.
   */
  static clone(instance: PositionBudget): PositionBudget {
    const cloned = Object.create(PositionBudget.prototype) as PositionBudget;
    Object.assign(cloned, instance);
    // Deep clone the athletes array to avoid shared references
    cloned.athletes = instance.athletes.map((athlete) => BudgetAthlete.clone(athlete));
    return cloned;
  }
}

// New Org Budgets
export interface ApiInstitutionalNil {
  budgeted_amount: number;
  committed_amount: number;
  contracted_amount: number;
  paid_amount: number;
}

export interface ApiScholarshipsAmount {
  budgeted: number;
  committed: number;
}


export interface ApiOrgBudgetOverview {
  cap: number;
  institutional_nil: ApiInstitutionalNil;
  // the monetary value of the scholarships, ie 100000
  scholarships_amount: ApiScholarshipsAmount;
  // The individual scholarships, ie 20/100
  scholarships_issued: ApiScholarshipsIssued;
  third_party_nil: number;
}

export class InstitutionalNilOverview {
  budgeted: number;
  committed: number;
  contracted: number;
  paid: number;

  constructor(data: ApiInstitutionalNil) {
    this.budgeted = convertCentsToDollarsDecimal(data.budgeted_amount);
    this.committed = convertCentsToDollarsDecimal(data.committed_amount);
    this.contracted = convertCentsToDollarsDecimal(data.contracted_amount);
    this.paid = convertCentsToDollarsDecimal(data.paid_amount);
  }
}



export class ScholarshipsAmount {
  budgeted: number;
  committed: number;

  constructor(data: ApiScholarshipsAmount) {
    this.budgeted = convertCentsToDollarsDecimal(data.budgeted);
    this.committed = convertCentsToDollarsDecimal(data.committed);
  }
}

export interface ApiScholarshipsIssued {
  budgeted: number;
  committed: number;
}
export class ScholarshipsIssued {
  budgeted: number;
  committed: number;

  constructor(data: ApiScholarshipsIssued) {
    this.budgeted = convertPercentageToDecimal(data.budgeted);
    this.committed = convertPercentageToDecimal(data.committed);
  }
}

export class OrgBudgetOverviewSummary {
  cap: number | null;
  institutionalNil: InstitutionalNilOverview;
  scholarshipsAmount: ScholarshipsAmount;
  scholarshipsIssued: ScholarshipsIssued;
  thirdPartyNil: number;

  constructor(data: ApiOrgBudgetOverview) {
    this.cap = data.cap ? convertCentsToDollarsDecimal(data.cap) : null;
    this.institutionalNil = new InstitutionalNilOverview(data.institutional_nil);
    this.scholarshipsAmount = new ScholarshipsAmount(data.scholarships_amount);
    this.scholarshipsIssued = new ScholarshipsIssued(data.scholarships_issued);
    this.thirdPartyNil = convertCentsToDollarsDecimal(data.third_party_nil);
  }
}


// @TODO: Update this to use real data from API
export interface ApiOrgBudgetList {
  data: ApiOrgBudget[];
  metadata: ApiPaginationMetadata;
}

export interface ApiOrgBudget {
  name: string;
  status: BudgetStatusEnum;
  updated_at: string;
  academic_year: string;
  twrn: string;
  team_twrn: string;
  institutional_nil: ApiInstitutionalNil;
  // the monetary value of the scholarships, ie 100000
  scholarships_amount: ApiScholarshipsAmount;
  // The individual scholarships, ie 20/100
  scholarships_issued: ApiScholarshipsIssued;
  third_party_nil: number;
  third_party_target: number;
  roster_size: number;
}

export class OrgBudget {
  name: string;
  status: BudgetStatusEnum;
  budget: {
    name: string;
    updatedAt: string;
  };

  teamTwrn: string;
  updatedAt: string;
  academicYearDateId: string;
  twrn: string;
  id: string;
  institutionalNil: InstitutionalNilOverview;
  scholarshipValue: ScholarshipsAmount;
  scholarships: ScholarshipsIssued;
  thirdPartyNil: number;
  thirdPartyTarget: number;
  rosterSize: number;

  constructor(data: ApiOrgBudget) {
    this.name = data.name;
    this.budget = {
      name: data.name,
      updatedAt: formatDateWithTime(new Date(data.updated_at), 'MMM dd, yyyy, h:mm aa'),
    };
    this.status = data.status;
    this.updatedAt = data.updated_at;
    this.academicYearDateId = data.academic_year;
    this.twrn = data.twrn;
    this.id = turnTwrnIntoId(data.twrn);
    this.institutionalNil = new InstitutionalNilOverview(data.institutional_nil);
    this.scholarshipValue = new ScholarshipsAmount(data.scholarships_amount);
    this.scholarships = new ScholarshipsIssued(data.scholarships_issued);
    this.thirdPartyNil = convertCentsToDollarsDecimal(data.third_party_nil);
    this.thirdPartyTarget = convertCentsToDollarsDecimal(data.third_party_target);
    this.teamTwrn = data.team_twrn;
    this.rosterSize = data.roster_size;
  }

  static mapBudgetToFormFields(budget: OrgBudget) {
    return {
      name: budget.name,
      academicYear: budget.academicYearDateId,
      team: budget.teamTwrn,
      totalBudget: budget.institutionalNil.budgeted,
      scholarships: budget.scholarships.budgeted,
      scholarshipValue: budget.scholarshipValue.budgeted,
      rosterSize: budget.rosterSize,
      thirdPartyTarget: budget.thirdPartyTarget,
    };
  }
}

// Allowed backend sort fields for Org Budgets list
export const ORG_BUDGET_SORT_FIELDS = [
  'budget_name',
  'team_name',
  'nil_budget_amount',
  'scholarships_amount',
  'scholarships_count',
  'third_party_nil',
] as const;

export type OrgBudgetSortField = typeof ORG_BUDGET_SORT_FIELDS[number];
